'use strict';

/* global angular */

function DataExportService ($rootScope, $window, AnalyticsService, Flux, IdentityService) {

  /** PRIVATE VARIABLES **/
  const service = this;
  const baseApi = IdentityService.getApiUri();
  const PlanStore = Flux.Store.get('PlanStore');

  let isOpen = false;
  const resolutionRegEx = /_(\d+|full)res/;

  const TOOLTIP_MESSAGES = {
    dxf: {
      default: 'Export a contour map of your site',
      disabled: 'Your contour map is processing'
    },
    pdf: {
      default: 'Export a PDF of your site',
      disabled: 'Your PDF is processing'
    }
  };

  const TYPES = {
    ORTHO: 'orthomosaic_image',
    CLOUD: '3d_point_cloud',
    DTM: 'dtm_image',
    DSM: 'dsm_image',
    MESH: '3d_mesh',
    DECIMATED: 'decimated_point_cloud',
    GROUND: 'gr_point_cloud'
  };

  const SUPPORTED_EXTENSIONS = ['las', 'tif', 'zip'];

  /** PRIVATE METHODS **/
  const sendAnalytics = (name, options) => {

    AnalyticsService.track('[CORE] Data Exports', name, options);
  };

  const findGeoid = (file) => {

    const ccrs = file.ccrs;

    return ccrs && ccrs.parameters ? ccrs.parameters.geoid : '';
  };

  const setGeoid = (files) => {

    let geoid;
    const file = files.find(findGeoid);

    if (file) {
      geoid = file.ccrs.parameters.geoid;
    }
    else {
      geoid = undefined;
    }

    return geoid;
  };

  const findBoxLink = (file) => {

    return file.boxShare;
  };

  const setBoxLink = (files) => {

    const file = files.find(findBoxLink);

    if (file && file.hasOwnProperty('boxShare')) {
      return file.boxShare.folder;
    }
    else {
      return '';
    }
  };

  const clearFiles = () => {

    service.projections = {};
    service.boxFolderLink = '';
    service.geoid = '';
    service.hasUtmPointCloud = false;
  };

  const getFileType = (type) => {

    switch (type) {
    case TYPES.GROUND:
      return TYPES.DTM;
    case TYPES.DECIMATED:
      return TYPES.CLOUD;
    default:
      return type;
    }
  };

  const sortResolutions = (a, b) => {

    // sort functions follow these rules:
    // if a - b < 0, sort a to a lower index than b
    // if a - b > 0, sort b to a lower index than a
    // if a - b === 0, no sorting occurs
    // some data captures have files with resolution of
    // 100 points/m^2 which resolve to 1 in this function
    // to ensure full res always appears first in the list
    // of resolutions, it needs to resolve to a value less
    // than 1 so that it sorts ahead of 100 points/m^2 res
    let resolutionA = resolutionRegEx.exec(a.location);
    let resolutionB = resolutionRegEx.exec(b.location);
    resolutionA = (!resolutionA || resolutionA[1] === 'full') ? 0.5 : parseInt(resolutionA[1], 10);
    resolutionB = (!resolutionB || resolutionB[1] === 'full') ? 0.5 : parseInt(resolutionB[1], 10);

    return resolutionA - resolutionB;
  };

  const groupByType = (projections) => {

    for (const key in projections) {
      if (projections.hasOwnProperty(key)) {
        const types = {};
        let projection = projections[key];

        projection.forEach((file) => {

          const type = getFileType(file.type);

          if (!types.hasOwnProperty(type)) {
            types[type] = type === TYPES.DTM || type === TYPES.CLOUD ? {} : [];
          }

          if (type !== TYPES.DTM && type !== TYPES.CLOUD) {
            types[type].push(file);
          }
          else {
            const fileExtension = `.${file.location.split('.').pop().toUpperCase()}`;

            if (!types[type].hasOwnProperty(fileExtension)) {
              types[type][fileExtension] = [];
            }

            types[type][fileExtension].push(file);

            if (fileExtension === '.LAS' && types[type][fileExtension].length >= 2) {
              types[type][fileExtension].sort(sortResolutions);
            }
          }
        });
        projections[key] = types;
      }
    }

    return projections;
  };

  /** PUBLIC VARIABLES **/
  service.TOOLTIPS = {
    delay: 1000,
    dxf: {
      message: TOOLTIP_MESSAGES.dxf.default,
      isProcessing: false
    },
    pdf: {
      message: TOOLTIP_MESSAGES.pdf.default,
      isProcessing: false
    }
  };

  service.ORDER = [TYPES.ORTHO, TYPES.CLOUD, TYPES.DTM, TYPES.DSM, TYPES.MESH];

  /** PUBLIC METHODS **/
  service.showResolutions = (type) => {

    return type === TYPES.DTM || type === TYPES.CLOUD;
  };

  service.showFiles = (type) => {

    return type !== TYPES.DTM || PlanStore.isHppPlan();
  };

  service.shouldShowContourButton = () => {

    return service.hasUtmPointCloud;
  };

  service.hasFiles = () => {

    return !angular.equals(service.projections, {});
  };

  service.hasOrthomosaic = () => {

    for (const projection in service.projections) {
      if (service.projections[projection].hasOwnProperty(TYPES.ORTHO)) {
        return true;
      }
    }

    return false;
  };

  service.getGeoid = () => {

    return service.geoid;
  };

  service.getBoxLink = () => {

    return service.boxFolderLink;
  };

  service.toggleExportProcessing = (type) => {

    type = type.toLowerCase();
    const tooltip = service.TOOLTIPS[type];

    tooltip.isProcessing = !tooltip.isProcessing;
    tooltip.isProcessing ? tooltip.message = TOOLTIP_MESSAGES[type].disabled : tooltip.message = TOOLTIP_MESSAGES[type].default;
  };

  service.resetExportProcessing = () => {

    service.TOOLTIPS.dxf.isProcessing = false;
    service.TOOLTIPS.pdf.isProcessing = false;
  };

  service.resetExportProcessing();

  service.sendOpenDropdownAnalytics = () => {

    isOpen = !isOpen;

    // we only want to send this when we first open the button so we differentiate between open and close clicks
    if (isOpen) {
      sendAnalytics('Open Export Button', {opened: true});
    }
  };

  service.downloadFile = (file) => {

    if (!file) {
      return;
    }

    const url = `${baseApi}${file.url}`;
    const location = file.location;
    const type = file.type;

    sendAnalytics('Download File', {
      location,
      type,
      url
    });

    $window.location.href = url;
  };

  service.openBoxFolder = () => {

    sendAnalytics('Open Box Folder', {
      boxFolderLink: service.boxFolderLink
    });

    $window.open(service.boxFolderLink, '_blank');
  };

  service.getFiles = (files) => {

    if (!files) {
      clearFiles();

      return;
    }

    const groupings = {};

    service.boxFolderLink = setBoxLink(files);
    service.geoid = setGeoid(files);

    files.forEach((file) => {
      if (file.location) {
        const extension = file.location.split('.').pop();
        if (SUPPORTED_EXTENSIONS.indexOf(extension) !== -1) {
          let groupName = 'UTM';

          if (file.hasOwnProperty('ccrs') && file.ccrs) {
            const ccrs = file.ccrs;

            // local projections use custom name
            groupName = ccrs.hasOwnProperty('name') ? ccrs.name : 'Unnamed projection';

            // for known coordinate systems, remove geoid from name
            // this name cannot be changed by user, so this is predictable behavior
            if (!ccrs.uuid && service.geoid) {
              const splitName = groupName.split('/');
              splitName.pop();
              groupName = splitName.join('/').trim();
            }
          }

          if (!groupings.hasOwnProperty(groupName)) {
            groupings[groupName] = [];
          }

          groupings[groupName].push(file);
        }
      }
    });

    let projections = groupByType(groupings);
    service.hasUtmPointCloud = projections.hasOwnProperty('UTM') && projections.UTM.hasOwnProperty(TYPES.CLOUD);

    if (Object.keys(projections).length > 1 && projections.hasOwnProperty('UTM')) {
      const mesh = projections.UTM[TYPES.MESH];
      delete projections.UTM;

      for (const projection in projections) {
        if (!projection.hasOwnProperty(TYPES.MESH)) {
          projections[projection][TYPES.MESH] = mesh;
        }
      }
    }
    service.projections = projections;
  };
}

DataExportService.$inject = ['$rootScope', '$window', 'AnalyticsService', 'sbFlux', 'IdentityService'];

module.exports = DataExportService;



// WEBPACK FOOTER //
// ./src/app/dash/map/services/DataExportService.js